import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { Component, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs';
import {
  LicenseBatchDto,
  ReleaseBundleDto,
} from 'src/app/models/license.model';
import {
  ArtifactResponse,
  SoftwareSelectionResolutionDto,
} from 'src/app/models/device.model';
import { LicenseBatchService } from 'src/app/services/license-batch.service';
import { matchesSearch } from 'src/app/utils/text-search';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';
import {
  DeviceSoftwareOverrideComponent,
  SoftwareOverridesSaved,
} from './device-software-override/device-software-override.component';

export interface DeviceLicenseManageModalData {
  deviceId: string;
  deviceName?: string;
  companyId?: string;
  linkedLicenseBatches?: LicenseBatchDto[];
  artifactOverrides?: ArtifactResponse[];
  bundleOverrides?: ReleaseBundleDto[];
  unresolvedOverrideIds?: string[];
  isMasterAdmin?: boolean;
  /** A masteradmin or production engineer, who reaches devices beyond their own company. */
  managesDevicesAcrossCompanies?: boolean;
}

export interface DeviceLicenseManageModalResult {
  linkedLicenseBatches: LicenseBatchDto[];
  licenseAssignmentsChanged: boolean;
  artifactOverrides?: ArtifactResponse[];
  bundleOverrides?: ReleaseBundleDto[];
  unresolvedOverrideIds?: string[];
  softwareSelectionResolutions?: SoftwareSelectionResolutionDto[];
}

type DeviceLicenseManageModalView = 'licenses' | 'softwareOverride';

@Component({
  selector: 'app-device-license-manage-modal',
  templateUrl: './device-license-manage-modal.component.html',
  styleUrls: ['./device-license-manage-modal.component.scss'],
  standalone: false,
})
export class DeviceLicenseManageModalComponent implements OnInit {
  private readonly licenseBatchService = inject(LicenseBatchService);
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  linkedLicenseBatches: LicenseBatchDto[] = [];
  availableLicenseBatches: LicenseBatchDto[] = [];
  searchText = '';
  loading = false;
  modalView: DeviceLicenseManageModalView = 'licenses';
  private licenseAssignmentsChanged = false;
  private readonly processingBatchIds = new Set<string>();

  @ViewChild(DeviceSoftwareOverrideComponent)
  softwareOverrideView?: DeviceSoftwareOverrideComponent;

  constructor(
    public dialogRef: DialogRef<DeviceLicenseManageModalResult>,
    @Inject(DIALOG_DATA) public data: DeviceLicenseManageModalData,
  ) {}

  ngOnInit(): void {
    this.linkedLicenseBatches = this.normalizeLicenseBatches(
      this.data.linkedLicenseBatches ?? [],
    );
    this.loadAvailableLicenseBatches();
  }

  get linkedCount(): number {
    return this.filteredLinkedLicenseBatches.length;
  }

  get availableCount(): number {
    return this.filteredAvailableLicenseBatches.length;
  }

  get hasPendingLicenseAssignments(): boolean {
    return this.processingBatchIds.size > 0;
  }

  get filteredLinkedLicenseBatches(): LicenseBatchDto[] {
    return this.filterBatches(this.linkedLicenseBatches);
  }

  get filteredAvailableLicenseBatches(): LicenseBatchDto[] {
    return this.filterBatches(this.availableLicenseBatches);
  }

  get modalTitle(): string {
    const deviceName = this.data.deviceName?.trim();
    const title =
      this.modalView === 'licenses'
        ? this.translate.instant('software_override.modal.manage_licenses')
        : this.translate.instant('software_override.modal.override_software');
    return deviceName ? `${title} - ${deviceName}` : title;
  }

  get modalDescription(): string {
    return this.modalView === 'licenses'
      ? this.translate.instant('software_override.modal.licenses_description')
      : this.translate.instant('software_override.modal.description');
  }

  close(overrides?: SoftwareOverridesSaved): void {
    if (this.hasPendingLicenseAssignments) {
      return;
    }

    this.dialogRef.close({
      linkedLicenseBatches: this.linkedLicenseBatches,
      licenseAssignmentsChanged: this.licenseAssignmentsChanged,
      ...(overrides ?? {}),
    });
  }

  refreshLicenseBatches(): void {
    this.loadAvailableLicenseBatches();
  }

  toggleModalView(): void {
    if (!this.data.isMasterAdmin || this.hasPendingLicenseAssignments) {
      return;
    }

    this.modalView =
      this.modalView === 'licenses' ? 'softwareOverride' : 'licenses';
  }

  showLicenseView(): void {
    if (this.hasPendingLicenseAssignments) {
      return;
    }

    this.modalView = 'licenses';
  }

  get footerActionTitle(): string {
    if (this.modalView === 'licenses') {
      return this.translate.instant(
        'software_override.modal.override_software',
      );
    }
    return this.softwareOverrideView?.saving
      ? this.translate.instant('software_override.actions.saving')
      : this.translate.instant('software_override.actions.save');
  }

  get footerActionDisabled(): boolean {
    if (this.hasPendingLicenseAssignments) {
      return true;
    }

    return (
      this.modalView === 'softwareOverride' &&
      (!this.softwareOverrideView?.hasOverrideChanges ||
        !!this.softwareOverrideView?.saving)
    );
  }

  handleFooterAction(): void {
    if (!this.data.isMasterAdmin) {
      return;
    }

    if (this.modalView === 'licenses') {
      this.toggleModalView();
      return;
    }

    this.softwareOverrideView?.saveOverrides();
  }

  isProcessing(batch: LicenseBatchDto): boolean {
    const batchId = batch.id?.trim();
    return !!batchId && this.processingBatchIds.has(batchId);
  }

  openBatchDetails(batch: LicenseBatchDto): void {
    const batchId = batch.id?.trim();
    if (!batchId) {
      return;
    }

    const route = this.data.isMasterAdmin
      ? ['/admin/licenses/batches', batchId]
      : ['/licenses/batches', batchId];
    const url = this.router.serializeUrl(this.router.createUrlTree(route));
    window.open(url, '_blank', 'noopener');
  }

  linkBatch(batch: LicenseBatchDto): void {
    const batchId = batch.id?.trim();
    const deviceId = this.data.deviceId?.trim();
    if (!batchId || !deviceId || this.processingBatchIds.has(batchId)) {
      return;
    }

    const batchSize = batch.licenseBatchSize ?? null;
    const assignedDevicesCount = this.getAssignedDevicesCount(batch);
    if (
      batchSize !== null &&
      batchSize > 0 &&
      assignedDevicesCount >= batchSize
    ) {
      this.showMessageModal(
        this.translate.instant('license_manager.messages.batch_size_title'),
        this.translate.instant('license_manager.messages.batch_size'),
      );
      return;
    }

    this.startBatchProcessing(batchId);
    this.licenseBatchService
      .assignDeviceToBatch(batchId, deviceId)
      .pipe(
        take(1),
        finalize(() => this.finishBatchProcessing(batchId)),
      )
      .subscribe({
        next: () => {
          const linkedBatch = this.withDeviceLinked(batch, deviceId);
          this.linkedLicenseBatches = this.sortBatchesByName([
            ...this.linkedLicenseBatches,
            linkedBatch,
          ]);
          this.availableLicenseBatches = this.availableLicenseBatches.filter(
            (existingBatch) => existingBatch.id !== batchId,
          );
          this.licenseAssignmentsChanged = true;
        },
        error: (error: unknown) => {
          this.showMessageModal(
            this.translate.instant('license_manager.messages.action_failed'),
            this.getErrorMessage(
              error,
              this.translate.instant('license_manager.errors.link'),
            ),
          );
        },
      });
  }

  unlinkBatch(batch: LicenseBatchDto): void {
    const batchId = batch.id?.trim();
    const deviceId = this.data.deviceId?.trim();
    if (!batchId || !deviceId || this.processingBatchIds.has(batchId)) {
      return;
    }

    this.startBatchProcessing(batchId);
    this.licenseBatchService
      .removeDeviceFromBatch(batchId, deviceId)
      .pipe(
        take(1),
        finalize(() => this.finishBatchProcessing(batchId)),
      )
      .subscribe({
        next: () => {
          const unlinkedBatch = this.withDeviceUnlinked(batch, deviceId);
          this.linkedLicenseBatches = this.linkedLicenseBatches.filter(
            (existingBatch) => existingBatch.id !== batchId,
          );
          this.availableLicenseBatches = this.sortBatchesByName([
            ...this.availableLicenseBatches,
            unlinkedBatch,
          ]);
          this.licenseAssignmentsChanged = true;
        },
        error: (error: unknown) => {
          this.showMessageModal(
            this.translate.instant('license_manager.messages.action_failed'),
            this.getErrorMessage(
              error,
              this.translate.instant('license_manager.errors.unlink'),
            ),
          );
        },
      });
  }

  getBatchTitle(batch: LicenseBatchDto): string {
    return (
      batch.name?.trim() ||
      this.translate.instant('license_manager.default_batch_name')
    );
  }

  trackByBatch(index: number, batch: LicenseBatchDto): string {
    return batch.id ?? `${batch.name ?? 'license-batch'}-${index}`;
  }

  private startBatchProcessing(batchId: string): void {
    this.processingBatchIds.add(batchId);
    this.updateDialogCloseState();
  }

  private finishBatchProcessing(batchId: string): void {
    this.processingBatchIds.delete(batchId);
    this.updateDialogCloseState();
  }

  private updateDialogCloseState(): void {
    this.dialogRef.disableClose = this.hasPendingLicenseAssignments;
  }

  private loadAvailableLicenseBatches(): void {
    this.loading = true;

    const companyId = this.data.companyId?.trim() ?? '';
    if (!companyId) {
      this.availableLicenseBatches = [];
      this.loading = false;
      this.showMessageModal(
        this.translate.instant(
          'license_manager.messages.missing_company_title',
        ),
        this.translate.instant('license_manager.messages.missing_company'),
      );
      return;
    }

    // Both endpoints answer with the batches of the company owning the device, but only
    // /licenses/batches/all is open to a production engineer, and only /licenses/batches to a
    // company administrator.
    const source$ = this.data.managesDevicesAcrossCompanies
      ? this.licenseBatchService.getAllForDevice(this.data.deviceId, 'BRIEF')
      : this.licenseBatchService.getAllByCurrentCompanyId('BRIEF');

    source$.pipe(take(1)).subscribe({
      next: (response) => {
        const linkedBatchIds = new Set(
          this.linkedLicenseBatches
            .map((batch) => batch.id?.trim() ?? '')
            .filter((batchId) => !!batchId),
        );
        this.availableLicenseBatches = this.sortBatchesByName(
          this.normalizeLicenseBatches(response.list ?? []).filter((batch) => {
            const batchId = batch.id?.trim();
            return !!batchId && !linkedBatchIds.has(batchId);
          }),
        );
        this.loading = false;
      },
      error: (error: unknown) => {
        this.availableLicenseBatches = [];
        this.loading = false;
        this.showMessageModal(
          this.translate.instant('license_manager.messages.load_failed'),
          this.getErrorMessage(
            error,
            this.translate.instant('license_manager.errors.load'),
          ),
        );
      },
    });
  }

  private showMessageModal(headerText: string, bodyText: string): void {
    this.dialog.open(InformationModalComponent, {
      data: {
        headerText,
        bodyText,
        cancelable: false,
      },
    });
  }

  private getErrorMessage(error: unknown, fallbackMessage: string): string {
    if (!error) {
      return fallbackMessage;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof error === 'object') {
      const typedError = error as {
        error?: {
          message?: string;
          title?: string;
          detail?: string;
        };
        message?: string;
      };

      return (
        typedError.error?.message?.trim() ||
        typedError.error?.detail?.trim() ||
        typedError.error?.title?.trim() ||
        typedError.message?.trim() ||
        fallbackMessage
      );
    }

    return fallbackMessage;
  }

  private filterBatches(batches: LicenseBatchDto[]): LicenseBatchDto[] {
    return batches.filter((batch) =>
      matchesSearch(this.searchText, [
        this.getBatchTitle(batch),
        batch.product?.name,
        batch.licensedProductId,
        batch.scope?.name,
        batch.scope?.id,
      ]),
    );
  }

  private normalizeLicenseBatches(
    batches: LicenseBatchDto[],
  ): LicenseBatchDto[] {
    return this.sortBatchesByName(
      batches.map((batch) => ({
        ...batch,
        scope: batch.scope ?? { id: '', name: '' },
      })),
    );
  }

  private sortBatchesByName(batches: LicenseBatchDto[]): LicenseBatchDto[] {
    return [...batches].sort((first, second) =>
      this.getBatchTitle(first).localeCompare(this.getBatchTitle(second)),
    );
  }

  private getAssignedDevicesCount(batch: LicenseBatchDto): number {
    const idsCount = batch.licensedDeviceIds?.length ?? 0;
    if (idsCount > 0) {
      return idsCount;
    }
    return batch.licensedDevices?.length ?? 0;
  }

  private withDeviceLinked(
    batch: LicenseBatchDto,
    deviceId: string,
  ): LicenseBatchDto {
    const existingIds = batch.licensedDeviceIds ?? [];
    return {
      ...batch,
      licensedDeviceIds: Array.from(new Set([...existingIds, deviceId])),
    };
  }

  private withDeviceUnlinked(
    batch: LicenseBatchDto,
    deviceId: string,
  ): LicenseBatchDto {
    return {
      ...batch,
      licensedDeviceIds: (batch.licensedDeviceIds ?? []).filter(
        (existingId) => existingId !== deviceId,
      ),
      licensedDevices: (batch.licensedDevices ?? []).filter(
        (device) => device.deviceId !== deviceId,
      ),
    };
  }
}
